import React, { useEffect, useRef, useState } from 'react';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import Table from '../../../core/common/dataTable/index';
import { formatError, formatSuccess } from '../../../core/data/redux/service/AuthService';
import {
  fetchMakerCheckerTemplate,
  fetchOrgPriorities,
  fetchOrgPrioritySuggestions,
  fetchOrgPrograms,
  fetchOrgStatusSuggestions,
  fetchOrgStatuses,
  fetchOrgWorkflow,
  fetchOrgWorkflows,
  approveOrgWorkflow,
  rejectOrgWorkflow,
  setOrgWorkflowActive,
  saveOrgPriority,
  saveOrgProgram,
  saveOrgStatus,
  saveOrgWorkflow,
} from '../../../core/data/redux/service/OrgMasterService';
import { AxiosResponse } from 'axios';

type Program = { key: string; name: string; description?: string; active: boolean };
type Priority = { key: string; name: string; level: number; active: boolean; suggestion?: boolean };
type StatusAction = {
  key: string;
  name: string;
  description?: string;
  startingStatus: boolean;
  finalStatus: boolean;
  active: boolean;
  suggestion?: boolean;
};
type WorkflowStatus = { key: string; label: string; startingStatus: boolean; endingStatus: boolean };
type WorkflowTransition = {
  fromStatusKey: string;
  toStatusKey: string;
  transitionName: string;
};
type WorkflowConfig = {
  key?: string;
  originalKey?: string;
  name: string;
  description: string;
  checkerRoleLabel?: string;
  approvalLabel?: string;
  pendingApproval?: boolean;
  active?: boolean;
  canApprove?: boolean;
  statuses: WorkflowStatus[];
  transitions: WorkflowTransition[];
};

const CHECKER_ROLE_LABEL = 'Organisation Admin / Platform Admin';

const emptyWorkflow = (): WorkflowConfig => ({
  name: '',
  description: '',
  checkerRoleLabel: CHECKER_ROLE_LABEL,
  statuses: [],
  transitions: [],
});

const emptyProgramForm = () => ({ key: '', name: '', description: '', active: true });
const emptyPriorityForm = () => ({ key: '', suggestionKey: '', name: '', level: 1, active: true });
const emptyStatusForm = () => ({
  key: '',
  suggestionKey: '',
  name: '',
  description: '',
  startingStatus: false,
  finalStatus: false,
  active: true,
});

type FocusPanel = 'program' | 'priority' | 'status' | 'workflow';

const OrgMasters = () => {
  const [activeTab, setActiveTab] = useState<'programs' | 'priorities' | 'statuses' | 'workflow'>('programs');
  const [focusedPanel, setFocusedPanel] = useState<FocusPanel | null>(null);
  const programFormRef = useRef<HTMLDivElement>(null);
  const priorityFormRef = useRef<HTMLDivElement>(null);
  const statusFormRef = useRef<HTMLDivElement>(null);
  const workflowFormRef = useRef<HTMLDivElement>(null);
  const focusTimeoutRef = useRef<number | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programForm, setProgramForm] = useState(emptyProgramForm());

  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [prioritySuggestions, setPrioritySuggestions] = useState<Priority[]>([]);
  const [priorityForm, setPriorityForm] = useState(emptyPriorityForm());

  const [statuses, setStatuses] = useState<StatusAction[]>([]);
  const [suggestions, setSuggestions] = useState<StatusAction[]>([]);
  const [statusForm, setStatusForm] = useState(emptyStatusForm());

  const [workflows, setWorkflows] = useState<WorkflowConfig[]>([]);
  const [workflowForm, setWorkflowForm] = useState<WorkflowConfig>(emptyWorkflow());
  const [refreshing, setRefreshing] = useState<'programs' | 'priorities' | 'statuses' | 'workflow' | null>(null);

  const selectedWorkflowStatusKeys = workflowForm.statuses.map(s => s.key);

  const loadPrograms = () =>
    fetchOrgPrograms()
      .then((res: AxiosResponse) => setPrograms(res.data || []));

  const loadPriorities = () =>
    Promise.all([
      fetchOrgPriorities().then((res: AxiosResponse) => setPriorities(res.data || [])),
      fetchOrgPrioritySuggestions().then((res: AxiosResponse) => setPrioritySuggestions(res.data || [])),
    ]);

  const loadStatuses = () =>
    Promise.all([
      fetchOrgStatuses().then((res: AxiosResponse) => setStatuses(res.data || [])),
      fetchOrgStatusSuggestions().then((res: AxiosResponse) => setSuggestions(res.data || [])),
    ]);

  const loadWorkflows = () =>
    Promise.all([
      fetchOrgWorkflows().then((res: AxiosResponse) => setWorkflows(res.data || [])),
      fetchOrgStatuses().then((res: AxiosResponse) => setStatuses(res.data || [])),
    ]);

  const refreshPrograms = () => {
    setRefreshing('programs');
    loadPrograms()
      .catch(err => formatError(err))
      .finally(() => setRefreshing(null));
  };

  const refreshPriorities = () => {
    setRefreshing('priorities');
    loadPriorities()
      .catch(err => formatError(err))
      .finally(() => setRefreshing(null));
  };

  const refreshStatuses = () => {
    setRefreshing('statuses');
    loadStatuses()
      .catch(err => formatError(err))
      .finally(() => setRefreshing(null));
  };

  const refreshWorkflows = () => {
    setRefreshing('workflow');
    loadWorkflows()
      .catch(err => formatError(err))
      .finally(() => setRefreshing(null));
  };

  useEffect(() => {
    Promise.all([loadPrograms(), loadPriorities(), loadStatuses(), loadWorkflows()])
      .catch(err => formatError(err));
  }, []);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        window.clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  const getPanelRef = (panel: FocusPanel) => {
    if (panel === 'program') return programFormRef;
    if (panel === 'priority') return priorityFormRef;
    if (panel === 'status') return statusFormRef;
    return workflowFormRef;
  };

  const scrollToPanel = (panel: FocusPanel) => {
    getPanelRef(panel).current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  };

  useEffect(() => {
    if (!focusedPanel) {
      return;
    }

    const panel = focusedPanel;
    const attemptScroll = (delay: number) =>
      window.setTimeout(() => scrollToPanel(panel), delay);

    const timers = [attemptScroll(0), attemptScroll(120), attemptScroll(300), attemptScroll(500)];

    return () => {
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, [focusedPanel, activeTab]);

  const focusFormPanel = (panel: FocusPanel) => {
    setFocusedPanel(panel);
    if (focusTimeoutRef.current) {
      window.clearTimeout(focusTimeoutRef.current);
    }
    focusTimeoutRef.current = window.setTimeout(() => setFocusedPanel(null), 650);
  };

  const formPanelClass = (panel: FocusPanel) =>
    `card mb-3 org-masters-form-panel${focusedPanel === panel ? ' org-masters-form-focus-flash' : ''}`;

  const RefreshButton = ({ onClick, loading }: { onClick: () => void; loading: boolean }) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      onClick={onClick}
      disabled={loading}
      title="Refresh"
    >
      <i className="ti ti-refresh" />
    </button>
  );

  const handleSaveProgram = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...(programForm.key ? { key: programForm.key } : {}),
      name: programForm.name,
      description: programForm.description,
      active: programForm.active,
    };
    saveOrgProgram(payload)
      .then(() => {
        formatSuccess(programForm.key ? 'Department updated' : 'Department saved');
        setProgramForm(emptyProgramForm());
        loadPrograms().catch(err => formatError(err));
      })
      .catch(err => formatError(err));
  };

  const editProgram = (program: Program) => {
    setProgramForm({
      key: program.key,
      name: program.name,
      description: program.description || '',
      active: program.active ?? true,
    });
    setActiveTab('programs');
    focusFormPanel('program');
  };

  const handleSavePriority = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...(priorityForm.key ? { key: priorityForm.key } : {}),
      ...(priorityForm.suggestionKey && !priorityForm.key ? { suggestionKey: priorityForm.suggestionKey } : {}),
      name: priorityForm.name,
      level: Number(priorityForm.level),
      active: priorityForm.active,
    };
    saveOrgPriority(payload)
      .then(() => {
        formatSuccess(priorityForm.key ? 'Priority updated' : 'Priority saved');
        setPriorityForm(emptyPriorityForm());
        loadPriorities().catch(err => formatError(err));
      })
      .catch(err => formatError(err));
  };

  const editPriority = (priority: Priority) => {
    setPriorityForm({
      key: priority.key,
      suggestionKey: '',
      name: priority.name,
      level: priority.level,
      active: priority.active ?? true,
    });
    setActiveTab('priorities');
    focusFormPanel('priority');
  };

  const populateFromPrioritySuggestion = (suggestion: Priority) => {
    if (priorities.some(p => p.name.toLowerCase() === suggestion.name.toLowerCase())) {
      return;
    }
    setPriorityForm({
      key: '',
      suggestionKey: suggestion.key,
      name: suggestion.name,
      level: suggestion.level,
      active: true,
    });
    focusFormPanel('priority');
  };

  const handleSaveStatus = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...(statusForm.key ? { key: statusForm.key } : {}),
      ...(statusForm.suggestionKey && !statusForm.key ? { suggestionKey: statusForm.suggestionKey } : {}),
      name: statusForm.name,
      description: statusForm.description,
      startingStatus: statusForm.startingStatus,
      finalStatus: statusForm.finalStatus,
      active: statusForm.active,
    };
    saveOrgStatus(payload)
      .then(() => {
        formatSuccess(statusForm.key ? 'Status updated' : 'Status added');
        setStatusForm(emptyStatusForm());
        loadStatuses().catch(err => formatError(err));
      })
      .catch(err => formatError(err));
  };

  const editStatus = (status: StatusAction) => {
    setStatusForm({
      key: status.key,
      suggestionKey: '',
      name: status.name,
      description: status.description || '',
      startingStatus: status.startingStatus,
      finalStatus: status.finalStatus,
      active: status.active,
    });
    setActiveTab('statuses');
    focusFormPanel('status');
  };

  const populateFromSuggestion = (suggestion: StatusAction) => {
    if (statuses.some(s => s.name.toLowerCase() === suggestion.name.toLowerCase())) {
      return;
    }
    setStatusForm({
      key: '',
      suggestionKey: suggestion.key,
      name: suggestion.name,
      description: suggestion.description || '',
      startingStatus: suggestion.startingStatus,
      finalStatus: suggestion.finalStatus,
      active: suggestion.active ?? true,
    });
    focusFormPanel('status');
  };

  const isEditingStatus = Boolean(statusForm.key);
  const isFromSuggestion = Boolean(statusForm.suggestionKey && !statusForm.key);
  const statusFormTitle = isEditingStatus
    ? 'Edit Status'
    : isFromSuggestion
      ? 'Add Status from Suggestion'
      : 'Add Custom Status';
  const statusSubmitLabel = isEditingStatus ? 'Update' : 'Add';

  const buildWorkflowStatuses = (selectedKeys: string[]): WorkflowStatus[] =>
    statuses
      .filter(s => selectedKeys.includes(s.key))
      .map(s => ({
        key: s.key,
        label: s.name,
        startingStatus: s.startingStatus,
        endingStatus: s.finalStatus,
      }));

  const toggleWorkflowStatus = (statusKey: string) => {
    setWorkflowForm(prev => {
      const isSelected = prev.statuses.some(s => s.key === statusKey);
      const nextKeys = isSelected
        ? prev.statuses.filter(s => s.key !== statusKey).map(s => s.key)
        : [...prev.statuses.map(s => s.key), statusKey];
      const nextStatuses = buildWorkflowStatuses(nextKeys);
      const validKeys = new Set(nextStatuses.map(s => s.key));
      return {
        ...prev,
        statuses: nextStatuses,
        transitions: prev.transitions.filter(t =>
          validKeys.has(t.fromStatusKey) && validKeys.has(t.toStatusKey)
        ),
      };
    });
  };

  const handleSaveWorkflow = (e: React.FormEvent) => {
    e.preventDefault();
    if (!workflowForm.statuses.length) {
      formatError('Select at least one organisation status for this workflow');
      return;
    }
    saveOrgWorkflow({
      originalKey: workflowForm.originalKey,
      name: workflowForm.name,
      description: workflowForm.description,
      statuses: workflowForm.statuses,
      transitions: workflowForm.transitions,
    })
      .then(() => {
        formatSuccess(workflowForm.originalKey
          ? 'Workflow updated and submitted for checker approval'
          : 'New workflow submitted for checker approval');
        setWorkflowForm(emptyWorkflow());
        loadWorkflows().catch(err => formatError(err));
      })
      .catch(err => formatError(err));
  };

  const handleApproveWorkflow = (workflowKey: string) => {
    approveOrgWorkflow(workflowKey)
      .then(() => {
        formatSuccess('Workflow approved and activated');
        loadWorkflows().catch(err => formatError(err));
      })
      .catch(err => formatError(err));
  };

  const handleRejectWorkflow = (workflowKey: string) => {
    rejectOrgWorkflow(workflowKey)
      .then(() => {
        formatSuccess('Workflow rejected');
        loadWorkflows().catch(err => formatError(err));
      })
      .catch(err => formatError(err));
  };

  const handleSetWorkflowActive = (workflowKey: string, active: boolean) => {
    setOrgWorkflowActive(workflowKey, active)
      .then(() => {
        formatSuccess(active ? 'Workflow activated' : 'Workflow deactivated');
        setWorkflowForm(prev =>
          prev.originalKey === workflowKey || prev.key === workflowKey
            ? { ...prev, active }
            : prev
        );
        loadWorkflows().catch(err => formatError(err));
      })
      .catch(err => formatError(err));
  };

  const loadMakerCheckerTemplate = () => {
    fetchMakerCheckerTemplate().then((res: AxiosResponse) => {
      const data = res.data;
      setWorkflowForm({
        ...data,
        originalKey: workflowForm.originalKey,
      });
      if (!(data.statuses || []).length) {
        formatError('Add statuses before configuring a workflow');
      }
    });
  };

  const editWorkflow = (workflowKey: string) => {
    setActiveTab('workflow');
    fetchOrgWorkflow(workflowKey).then((res: AxiosResponse) => {
      setWorkflowForm({
        ...res.data,
        originalKey: res.data.key,
      });
      focusFormPanel('workflow');
    });
  };

  const addTransition = () => {
    setWorkflowForm(prev => ({
      ...prev,
      transitions: [...prev.transitions, {
        fromStatusKey: '',
        toStatusKey: '',
        transitionName: '',
      }],
    }));
  };

  const approvalBadgeClass = (label?: string) => {
    if (label === 'Approved') return 'badge bg-success';
    if (label === 'Rejected') return 'badge bg-danger';
    return 'badge bg-warning text-dark';
  };

  const workflowStatusOptions = workflowForm.statuses;
  const addedSuggestionLabels = new Set(statuses.map(s => s.name.toLowerCase()));
  const addedPriorityLabels = new Set(priorities.map(p => p.name.toLowerCase()));
  const isEditingPriority = Boolean(priorityForm.key);
  const isFromPrioritySuggestion = Boolean(priorityForm.suggestionKey && !priorityForm.key);
  const priorityFormTitle = isEditingPriority
    ? 'Edit Priority'
    : isFromPrioritySuggestion
      ? 'Add Priority from Suggestion'
      : 'Add Priority';

  const programColumns = [
    { title: 'Department Name', dataIndex: 'name', sorter: (a: Program, b: Program) => a.name.localeCompare(b.name) },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Active', dataIndex: 'active', render: (v: boolean) => v ? 'Yes' : 'No' },
    {
      title: 'Actions',
      dataIndex: 'key',
      render: (_: string, row: Program) => (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => editProgram(row)}>
          Edit
        </button>
      ),
    },
  ];

  const priorityColumns = [
    { title: 'Priority', dataIndex: 'name', sorter: (a: Priority, b: Priority) => a.name.localeCompare(b.name) },
    { title: 'Level', dataIndex: 'level', sorter: (a: Priority, b: Priority) => a.level - b.level },
    { title: 'Active', dataIndex: 'active', render: (v: boolean) => v ? 'Yes' : 'No' },
    {
      title: 'Actions',
      dataIndex: 'key',
      render: (_: string, row: Priority) => (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => editPriority(row)}>
          Edit
        </button>
      ),
    },
  ];

  const statusColumns = [
    { title: 'Status', dataIndex: 'name', sorter: (a: StatusAction, b: StatusAction) => a.name.localeCompare(b.name) },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Starting', dataIndex: 'startingStatus', render: (v: boolean) => v ? 'Yes' : 'No' },
    { title: 'Final', dataIndex: 'finalStatus', render: (v: boolean) => v ? 'Yes' : 'No' },
    { title: 'Active', dataIndex: 'active', render: (v: boolean) => v ? 'Yes' : 'No' },
    {
      title: 'Actions',
      dataIndex: 'key',
      render: (_: string, row: StatusAction) => (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => editStatus(row)}>
          Edit
        </button>
      ),
    },
  ];

  const workflowColumns = [
    { title: 'Name', dataIndex: 'name', sorter: (a: WorkflowConfig, b: WorkflowConfig) => a.name.localeCompare(b.name) },
    {
      title: 'Approval',
      dataIndex: 'approvalLabel',
      render: (label: string) => <span className={approvalBadgeClass(label)}>{label || 'Pending'}</span>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      render: (active: boolean) => active
        ? <span className="badge bg-success">Yes</span>
        : <span className="badge bg-secondary">No</span>,
    },
    { title: 'Checker', dataIndex: 'checkerRoleLabel' },
    { title: 'Statuses', dataIndex: 'statuses', render: (_: unknown, row: WorkflowConfig) => row.statuses?.length || 0 },
    { title: 'Transitions', dataIndex: 'transitions', render: (_: unknown, row: WorkflowConfig) => row.transitions?.length || 0 },
    {
      title: 'Actions',
      dataIndex: 'key',
      render: (_: string, row: WorkflowConfig) => (
        <div className="d-flex gap-1 flex-wrap align-items-center">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => editWorkflow(row.key!)}>
            Edit
          </button>
          {row.approvalLabel === 'Approved' && (
            <div className="form-check mb-0 ms-1">
              <input
                type="checkbox"
                className="form-check-input"
                id={`wf-active-${row.key}`}
                checked={Boolean(row.active)}
                onChange={e => handleSetWorkflowActive(row.key!, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`wf-active-${row.key}`}>Active</label>
            </div>
          )}
          {row.canApprove && row.pendingApproval && (
            <>
              <button type="button" className="btn btn-sm btn-success" onClick={() => handleApproveWorkflow(row.key!)}>
                Approve
              </button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRejectWorkflow(row.key!)}>
                Reject
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <style>{`
        @keyframes orgMastersFormFocus {
          0% {
            background-color: rgba(13, 110, 253, 0.2);
            box-shadow: 0 0 0 0.35rem rgba(13, 110, 253, 0.3);
          }
          100% {
            background-color: transparent;
            box-shadow: none;
          }
        }
        .org-masters-form-panel {
          scroll-margin-top: 90px;
        }
        .org-masters-form-focus-flash {
          animation: orgMastersFormFocus 0.6s ease-out forwards;
          border: 2px solid rgba(13, 110, 253, 0.85) !important;
        }
        .org-masters-form-focus-flash > .card-header,
        .org-masters-form-focus-flash > .card-body {
          animation: orgMastersFormFocus 0.6s ease-out forwards;
        }
      `}</style>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Organisation Masters</h2>
          </div>
          <div className="head-icons ms-2"><CollapseHeader /></div>
        </div>

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === 'programs' ? 'active' : ''}`} onClick={() => setActiveTab('programs')}>
              Departments
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === 'priorities' ? 'active' : ''}`} onClick={() => setActiveTab('priorities')}>
              Priorities
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === 'statuses' ? 'active' : ''}`} onClick={() => setActiveTab('statuses')}>
              Statuses
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === 'workflow' ? 'active' : ''}`} onClick={() => setActiveTab('workflow')}>
              Workflows
            </button>
          </li>
        </ul>

        {activeTab === 'programs' && (
          <>
            <div ref={programFormRef} className={formPanelClass('program')}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{programForm.key ? 'Edit Department' : 'Add Department'}</h5>
                {programForm.key && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                    setProgramForm(emptyProgramForm());
                    setFocusedPanel(null);
                  }}>
                    New Department
                  </button>
                )}
              </div>
              <div className="card-body">
                <form className="row g-3" onSubmit={handleSaveProgram}>
                  <div className="col-md-4">
                    <label className="form-label">Department Name</label>
                    <input className="form-control" value={programForm.name}
                      onChange={e => setProgramForm({ ...programForm, name: e.target.value })} required />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Description</label>
                    <input className="form-control" value={programForm.description}
                      onChange={e => setProgramForm({ ...programForm, description: e.target.value })} />
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <div className="form-check mb-2">
                      <input type="checkbox" className="form-check-input" id="program-active"
                        checked={programForm.active}
                        onChange={e => setProgramForm({ ...programForm, active: e.target.checked })} />
                      <label className="form-check-label" htmlFor="program-active">Active</label>
                    </div>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button type="submit" className="btn btn-primary w-100">
                      {programForm.key ? 'Update Department' : 'Add Department'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Organisation Departments</h5>
                <RefreshButton onClick={refreshPrograms} loading={refreshing === 'programs'} />
              </div>
              <div className="card-body">
                <Table dataSource={programs} columns={programColumns} Selection={false} rowKey="key" />
              </div>
            </div>
          </>
        )}

        {activeTab === 'priorities' && (
          <>
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Suggested Priorities</h5>
                <RefreshButton onClick={refreshPriorities} loading={refreshing === 'priorities'} />
              </div>
              <div className="card-body">
                <p className="text-muted small mb-2">Click a suggestion to fill the form below, then review and add.</p>
                <div className="d-flex flex-wrap gap-2">
                  {prioritySuggestions.map(s => (
                    <button
                      key={s.key}
                      type="button"
                      className={`btn btn-sm ${
                        addedPriorityLabels.has(s.name.toLowerCase())
                          ? 'btn-success'
                          : priorityForm.suggestionKey === s.key
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                      }`}
                      disabled={addedPriorityLabels.has(s.name.toLowerCase())}
                      onClick={() => populateFromPrioritySuggestion(s)}
                    >
                      {s.name} (L{s.level}){addedPriorityLabels.has(s.name.toLowerCase()) ? ' ✓' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div ref={priorityFormRef} className={formPanelClass('priority')}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{priorityFormTitle}</h5>
                {(isEditingPriority || isFromPrioritySuggestion) && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                    setPriorityForm(emptyPriorityForm());
                    setFocusedPanel(null);
                  }}>
                    New Priority
                  </button>
                )}
              </div>
              <div className="card-body">
                <form className="row g-3" onSubmit={handleSavePriority}>
                  <div className="col-md-4">
                    <label className="form-label">Priority Name</label>
                    <input className="form-control" value={priorityForm.name}
                      onChange={e => setPriorityForm({ ...priorityForm, name: e.target.value })} required />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Level</label>
                    <input
                      type="number"
                      min={1}
                      className="form-control"
                      value={priorityForm.level}
                      onChange={e => setPriorityForm({ ...priorityForm, level: Number(e.target.value) || 1 })}
                      required
                    />
                    <div className="form-text">Lower level = higher urgency</div>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <div className="form-check mb-2">
                      <input type="checkbox" className="form-check-input" id="priority-active"
                        checked={priorityForm.active}
                        onChange={e => setPriorityForm({ ...priorityForm, active: e.target.checked })} />
                      <label className="form-check-label" htmlFor="priority-active">Active</label>
                    </div>
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <button type="submit" className="btn btn-primary w-100">
                      {isEditingPriority ? 'Update Priority' : 'Add Priority'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Organisation Priorities</h5>
                <RefreshButton onClick={refreshPriorities} loading={refreshing === 'priorities'} />
              </div>
              <div className="card-body">
                <Table dataSource={priorities} columns={priorityColumns} Selection={false} rowKey="key" />
              </div>
            </div>
          </>
        )}

        {activeTab === 'statuses' && (
          <>
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Suggested Statuses</h5>
                <RefreshButton onClick={refreshStatuses} loading={refreshing === 'statuses'} />
              </div>
              <div className="card-body">
                <p className="text-muted small mb-2">Click a suggestion to fill the form below, then review and add.</p>
                <div className="d-flex flex-wrap gap-2">
                  {suggestions.map(s => (
                    <button
                      key={s.key}
                      type="button"
                      className={`btn btn-sm ${
                        addedSuggestionLabels.has(s.name.toLowerCase())
                          ? 'btn-success'
                          : statusForm.suggestionKey === s.key
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                      }`}
                      disabled={addedSuggestionLabels.has(s.name.toLowerCase())}
                      onClick={() => populateFromSuggestion(s)}
                    >
                      {s.name}{addedSuggestionLabels.has(s.name.toLowerCase()) ? ' ✓' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div ref={statusFormRef} className={formPanelClass('status')}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{statusFormTitle}</h5>
                {(isEditingStatus || isFromSuggestion) && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                    setStatusForm(emptyStatusForm());
                    setFocusedPanel(null);
                  }}>
                    New Status
                  </button>
                )}
              </div>
              <div className="card-body">
                <form className="row g-3" onSubmit={handleSaveStatus}>
                  <div className="col-md-3">
                    <label className="form-label">Status Name</label>
                    <input className="form-control" value={statusForm.name}
                      onChange={e => setStatusForm({ ...statusForm, name: e.target.value, suggestionKey: '' })} required />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Description</label>
                    <input className="form-control" value={statusForm.description}
                      onChange={e => setStatusForm({ ...statusForm, description: e.target.value })} />
                  </div>
                  <div className="col-md-2 form-check mt-4">
                    <input type="checkbox" className="form-check-input" id="status-starting"
                      checked={statusForm.startingStatus}
                      onChange={e => setStatusForm({ ...statusForm, startingStatus: e.target.checked })} />
                    <label className="form-check-label" htmlFor="status-starting">Starting</label>
                  </div>
                  <div className="col-md-2 form-check mt-4">
                    <input type="checkbox" className="form-check-input" id="status-final"
                      checked={statusForm.finalStatus}
                      onChange={e => setStatusForm({ ...statusForm, finalStatus: e.target.checked })} />
                    <label className="form-check-label" htmlFor="status-final">Final</label>
                  </div>
                  <div className="col-md-1 form-check mt-4">
                    <input type="checkbox" className="form-check-input" id="status-active"
                      checked={statusForm.active}
                      onChange={e => setStatusForm({ ...statusForm, active: e.target.checked })} />
                    <label className="form-check-label" htmlFor="status-active">Active</label>
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <button type="submit" className="btn btn-primary w-100">
                      {statusSubmitLabel}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Statuses</h5>
                <RefreshButton onClick={refreshStatuses} loading={refreshing === 'statuses'} />
              </div>
              <div className="card-body">
                <Table dataSource={statuses} columns={statusColumns} Selection={false} rowKey="key" />
              </div>
            </div>
          </>
        )}

        {activeTab === 'workflow' && (
          <>
            {statuses.length === 0 && (
              <div className="alert alert-warning">Add statuses in the Statuses tab before configuring a workflow.</div>
            )}
            <div className="alert alert-info py-2">
              All new and updated workflows are saved as <strong>Pending</strong> and cannot be activated until a different organisation or platform admin approves them.
              You cannot approve a workflow you submitted yourself. Only one workflow can be active per organisation at a time.
            </div>
            <div ref={workflowFormRef} className={formPanelClass('workflow')}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{workflowForm.originalKey ? 'Edit Workflow' : 'Create Workflow'}</h5>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadMakerCheckerTemplate}>
                    Load Maker-Checker Template
                  </button>
                  {workflowForm.originalKey && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => {
                      setWorkflowForm(emptyWorkflow());
                      setFocusedPanel(null);
                    }}>
                      New Workflow
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveWorkflow}>
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Workflow Name</label>
                      <input className="form-control" value={workflowForm.name}
                        onChange={e => setWorkflowForm({ ...workflowForm, name: e.target.value })} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Checker (workflow approval)</label>
                      <input className="form-control" value={workflowForm.checkerRoleLabel || CHECKER_ROLE_LABEL} readOnly />
                      <small className="text-muted">Fixed to organisation admin or platform admin</small>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Description</label>
                      <input className="form-control" value={workflowForm.description}
                        onChange={e => setWorkflowForm({ ...workflowForm, description: e.target.value })} />
                    </div>
                  </div>
                  {workflowForm.approvalLabel && (
                    <p className="text-muted small mb-3">
                      Current approval: <span className={approvalBadgeClass(workflowForm.approvalLabel)}>{workflowForm.approvalLabel}</span>
                      {workflowForm.active && <span className="badge bg-success ms-2">Active</span>}
                    </p>
                  )}

                  {workflowForm.originalKey && (
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="workflow-active"
                        checked={Boolean(workflowForm.active)}
                        disabled={workflowForm.approvalLabel !== 'Approved'}
                        onChange={e => handleSetWorkflowActive(workflowForm.originalKey!, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="workflow-active">Active</label>
                      {workflowForm.approvalLabel !== 'Approved' && (
                        <div className="form-text">Only approved workflows can be activated. Activating one deactivates others.</div>
                      )}
                    </div>
                  )}

                  <h6 className="mb-2">Select Statuses</h6>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {statuses.filter(s => s.active).map(s => (
                      <button
                        key={s.key}
                        type="button"
                        className={`btn btn-sm ${selectedWorkflowStatusKeys.includes(s.key) ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => toggleWorkflowStatus(s.key)}
                      >
                        {s.name}
                        {s.startingStatus && ' (start)'}
                        {s.finalStatus && ' (final)'}
                      </button>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
                    <h6 className="mb-0">Transitions</h6>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addTransition}>Add Transition</button>
                  </div>
                  {workflowForm.transitions.map((transition, idx) => (
                    <div className="row g-2 mb-2 align-items-center" key={`transition-${idx}`}>
                      <div className="col-md-3">
                        <select className="form-select" value={transition.fromStatusKey}
                          onChange={e => {
                            const value = e.target.value;
                            setWorkflowForm(prev => {
                              const transitions = [...prev.transitions];
                              transitions[idx] = { ...transitions[idx], fromStatusKey: value };
                              return { ...prev, transitions };
                            });
                          }} required>
                          <option value="">From</option>
                          {workflowStatusOptions.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <select className="form-select" value={transition.toStatusKey}
                          onChange={e => {
                            const value = e.target.value;
                            setWorkflowForm(prev => {
                              const transitions = [...prev.transitions];
                              transitions[idx] = { ...transitions[idx], toStatusKey: value };
                              return { ...prev, transitions };
                            });
                          }} required>
                          <option value="">To</option>
                          {workflowStatusOptions.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <input className="form-control" placeholder="Transition name" value={transition.transitionName}
                          onChange={e => {
                            const value = e.target.value;
                            setWorkflowForm(prev => {
                              const transitions = [...prev.transitions];
                              transitions[idx] = { ...transitions[idx], transitionName: value };
                              return { ...prev, transitions };
                            });
                          }} required />
                      </div>
                      <div className="col-md-2">
                        <button type="button" className="btn btn-sm btn-outline-danger w-100"
                          onClick={() => setWorkflowForm(prev => ({
                            ...prev,
                            transitions: prev.transitions.filter((_, i) => i !== idx),
                          }))}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  <button type="submit" className="btn btn-primary mt-3" disabled={!statuses.length}>
                    Submit for Approval
                  </button>
                </form>
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Configured Workflows</h5>
                <RefreshButton onClick={refreshWorkflows} loading={refreshing === 'workflow'} />
              </div>
              <div className="card-body">
                <Table dataSource={workflows} columns={workflowColumns} Selection={false} rowKey="key" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrgMasters;
